/* ============================================================
   AmenTee — Sticky ATC ↔ Size Gate bridge
   assets/amb-sticky-atc.js

   The size gate (snippets/at-size-gate.liquid) intercepts clicks on
   [name="add"] inside <product-info>. The sticky bar's button lives
   outside that element and submits the form remotely via its `form`
   attribute, so the gate never sees it. This wires the two together:

   1. Blocks the sticky button while no size is chosen.
   2. Scrolls the size picker into view so the gate's inline error is
      actually on screen when it appears.
   3. Delegates to the real add-to-cart button, so the gate renders its
      own message and fires its own size_gate_blocked analytics event —
      one code path, no duplicated logic.
   4. Rewrites the sticky bar's stale "Size: S" (server-rendered from the
      default variant) to a "Choose a size" prompt while blocked.

   INSTALL — layout/theme.liquid, before </head>:
     <script src="{{ 'amb-sticky-atc.js' | asset_url }}" defer></script>
   ============================================================ */

(function () {
  'use strict';

  var SIZE_LABEL = /^\s*(size|talla|tama|gr(ö|oe|o)(ß|ss)e|taille|taglia|maat|storlek)\b/i;
  var PROMPT = 'Choose a size';
  var MESSAGE = 'Pick a size first — scrolling you there now.';
  var BLOCKED_CLASS = 'at-sizegate-blocked';

  var applying = false;
  var observer = null;

  function productRoot() {
    return document.querySelector('product-info');
  }

  /* ---- Is a size chosen? ----
     Read the picker directly rather than trusting a class from the gate
     snippet — installed gate versions differ, but "no size input is
     checked" is true on every one of them. */

  function sizeControls() {
    var field = sizeField();
    if (!field) return [];
    var nodes = field.querySelectorAll('input[type="radio"], select');
    var out = [];
    for (var i = 0; i < nodes.length; i++) {
      // The size-chart popup carries its own in/cm radios — ignore those.
      if (nodes[i].closest('modal-dialog, .product-popup-modal, .size-chart-container')) continue;
      out.push(nodes[i]);
    }
    return out;
  }

  function isBlocked() {
    var root = productRoot();
    if (!root) return false;

    // Honour the class too, for gate versions that do set it.
    if (root.classList.contains(BLOCKED_CLASS)) return true;

    var controls = sizeControls();
    if (!controls.length) return false; // no size option on this product

    var radios = [], selects = [];
    for (var i = 0; i < controls.length; i++) {
      (controls[i].tagName === 'SELECT' ? selects : radios).push(controls[i]);
    }

    if (radios.length) {
      for (var j = 0; j < radios.length; j++) {
        if (radios[j].checked) return false;
      }
      return true;
    }

    if (selects.length) {
      var sel = selects[0];
      var chosen = sel.options && sel.options[sel.selectedIndex];
      if (!sel.value) return true;
      if (chosen && /choose|select/i.test(chosen.text)) return true;
    }

    return false;
  }

  function stickyBar() {
    var btn = document.querySelector('.sticky-atc-button');
    return btn ? btn.closest('.content-container') : null;
  }

  function prefersReducedMotion() {
    return !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  }

  /* ---- Swap the stale size value for a prompt while blocked ---- */

  function sync() {
    var bar = stickyBar();
    if (!bar) return;

    var blocked = isBlocked();
    applying = true;

    bar.classList.toggle('amb-atc-needs-size', blocked);
    if (!blocked) { hideMessage(bar); clearHighlight(); }

    var options = bar.querySelectorAll('.sticky-atc-active-variant__option');
    for (var i = 0; i < options.length; i++) {
      var opt = options[i];
      var name = opt.querySelector('.sticky-atc-active-variant__option-name');
      var value = opt.querySelector('.sticky-atc-active-variant__option-value');
      if (!name || !value || !SIZE_LABEL.test(name.textContent)) continue;

      if (blocked) {
        if (!opt.hasAttribute('data-amb-prompt')) {
          opt.setAttribute('data-amb-prompt', '');
          value.setAttribute('data-at-real', value.textContent);
          value.textContent = PROMPT;
        }
      } else if (opt.hasAttribute('data-amb-prompt')) {
        opt.removeAttribute('data-amb-prompt');
        if (value.hasAttribute('data-at-real')) {
          value.textContent = value.getAttribute('data-at-real');
          value.removeAttribute('data-at-real');
        }
      }
    }

    applying = false;
  }

  /* ---- Find the size picker so we can scroll to it ---- */

  function sizeField() {
    var root = productRoot();
    if (!root) return null;

    // The gate injects its error inside the size fieldset — cheapest hook.
    var err = root.querySelector('.at-sizegate-error');
    if (err) return err.closest('fieldset, .product-form__input') || err.parentElement;

    var fields = root.querySelectorAll('fieldset.product-form__input, .product-form__input');
    for (var i = 0; i < fields.length; i++) {
      var label = fields[i].querySelector('legend, .form__label');
      if (label && SIZE_LABEL.test(label.textContent)) return fields[i];
    }
    return null;
  }

  function realAddButton() {
    var root = productRoot();
    if (!root) return null;
    return root.querySelector('[name="add"]:not(.sticky-atc-button)');
  }

  /* ---- A message at the bar itself, not 800px up the page ---- */

  var messageTimer = null;

  function messageEl(bar) {
    var el = bar.querySelector('.amb-atc-gate-msg');
    if (el) return el;
    el = document.createElement('div');
    el.className = 'amb-atc-gate-msg';
    el.setAttribute('role', 'status');
    el.hidden = true;
    bar.insertBefore(el, bar.firstChild);
    return el;
  }

  function showMessage(bar) {
    if (!bar) return;
    var el = messageEl(bar);
    el.textContent = MESSAGE;
    el.hidden = false;
    window.clearTimeout(messageTimer);
    messageTimer = window.setTimeout(function () { hideMessage(bar); }, 6000);
  }

  function hideMessage(bar) {
    if (!bar) return;
    var el = bar.querySelector('.amb-atc-gate-msg');
    if (el) el.hidden = true;
    window.clearTimeout(messageTimer);
  }

  /* ---- Fire the gate's own response (yellow flash on the main button) ----
     Guarded: a temporary capture-phase submit blocker sits on the product
     form for the duration of the click. If the gate intercepts, nothing
     submits anyway. If no gate is handling this product, the blocker stops
     the form rather than letting an unsized variant reach the cart. */

  function delegateToGate() {
    var real = realAddButton();
    if (!real || real.disabled) return;

    var form = real.form || (productRoot() && productRoot().querySelector('form'));
    var block = function (e) { e.preventDefault(); e.stopImmediatePropagation(); };

    if (form) form.addEventListener('submit', block, true);
    try {
      real.click();
    } finally {
      window.setTimeout(function () {
        if (form) form.removeEventListener('submit', block, true);
      }, 0);
    }
  }

  /* ---- Mark the picker itself, so the ask is obvious on arrival ---- */

  var calloutTimer = null;

  function highlightSizeField(field) {
    if (!field) return;
    field.classList.remove('amb-atc-size-callout');
    void field.offsetWidth;
    field.classList.add('amb-atc-size-callout');
    window.clearTimeout(calloutTimer);
    calloutTimer = window.setTimeout(function () {
      field.classList.remove('amb-atc-size-callout');
    }, 3200);
  }

  function clearHighlight() {
    var field = sizeField();
    if (field) field.classList.remove('amb-atc-size-callout');
    window.clearTimeout(calloutTimer);
  }

  /* ---- What happens when someone taps the sticky button too early ---- */

  function promptForSize() {
    var reduce = prefersReducedMotion();
    var field = sizeField();
    var bar = stickyBar();

    showMessage(bar);

    if (bar) {
      bar.classList.remove('amb-atc-shake');
      void bar.offsetWidth; // restart the animation
      bar.classList.add('amb-atc-shake');
    }

    if (field && field.scrollIntoView) {
      field.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'center' });
    }

    highlightSizeField(field);
    delegateToGate();

    window.setTimeout(function () {
      if (!field) return;
      var first = field.querySelector('input:not([disabled]), select:not([disabled]), label');
      if (!first || !first.focus) return;
      try { first.focus({ preventScroll: true }); } catch (e) { first.focus(); }
    }, reduce ? 0 : 450);
  }

  /* ============================================================
     Successful-add feedback

     Dawn's ProductForm puts its loading state on the submit button inside
     the form. The sticky button submits remotely via its `form` attribute,
     so it never receives any of that — the request goes out and the bar
     looks inert. We supply our own.
     ============================================================ */

  var addTimeout = null;
  var addStartedAt = 0;
  var tickTimeout = null;
  var MIN_SPIN = 450;   // below this the spinner just flickers
  var TICK_MS = 1200;

  /* The bar can be replaced by a section re-render mid-sequence, which would
     leave our timers stripping classes off a detached node while a fresh
     button sits in the page. Always act on the button that's live now. */
  function liveButton(preferred) {
    if (preferred && preferred.isConnected) return preferred;
    return document.querySelector('.sticky-atc-button');
  }

  /* Notice cart adds without depending on the theme's internal pubsub. */
  function watchCartAdds() {
    if (!window.fetch || window.fetch.__ambPatched) return;

    var original = window.fetch;
    var patched = function (input, init) {
      var url = '';
      try {
        url = typeof input === 'string' ? input : (input && input.url) || '';
      } catch (e) { /* opaque input, ignore */ }

      var result = original.apply(this, arguments);

      if (/\/cart\/add/.test(url)) {
        result.then(function (res) {
          document.dispatchEvent(new CustomEvent('amb:cart-added', { detail: { ok: !!(res && res.ok) } }));
        }, function () {
          document.dispatchEvent(new CustomEvent('amb:cart-added', { detail: { ok: false } }));
        });
      }
      return result;
    };
    patched.__ambPatched = true;
    window.fetch = patched;
  }

  function startAdding(button) {
    if (!button) return;
    window.clearTimeout(tickTimeout);
    addStartedAt = Date.now();

    button.classList.remove('amb-atc-added');
    button.classList.add('amb-atc-loading');
    button.setAttribute('aria-busy', 'true');

    // If nothing reports back (full page POST, XHR theme, network stall),
    // don't leave a spinner running forever.
    window.clearTimeout(addTimeout);
    addTimeout = window.setTimeout(function () { finishAdding(button, false); }, 6000);
  }

  function finishAdding(button, ok) {
    var target = liveButton(button);
    if (!target) return;

    // Hold the spinner long enough to read as a spinner rather than a blink.
    var elapsed = Date.now() - addStartedAt;
    if (elapsed < MIN_SPIN) {
      window.clearTimeout(addTimeout);
      addTimeout = window.setTimeout(function () { finishAdding(button, ok); }, MIN_SPIN - elapsed);
      return;
    }

    window.clearTimeout(addTimeout);
    target.classList.remove('amb-atc-loading');
    target.removeAttribute('aria-busy');

    if (!ok) return;

    target.classList.add('amb-atc-added');
    window.clearTimeout(tickTimeout);
    tickTimeout = window.setTimeout(function () {
      var later = liveButton(target);
      if (later) later.classList.remove('amb-atc-added');
    }, TICK_MS);

    flyToCart();
  }

  function cartIcon() {
    return document.querySelector('#cart-icon-bubble, .header__icon--cart, a[href$="/cart"]');
  }

  /* Send the product thumbnail to the cart icon — the "yes, that worked"
     signal, and it points at where the item went. */
  function flyToCart() {
    if (prefersReducedMotion()) return;

    var bar = stickyBar();
    var source = bar && bar.querySelector('img');
    var target = cartIcon();
    if (!source || !target || !source.animate) return;

    var from = source.getBoundingClientRect();
    var to = target.getBoundingClientRect();
    if (!from.width || !to.width) return;

    var ghost = source.cloneNode(true);
    ghost.removeAttribute('id');
    ghost.style.cssText = [
      'position:fixed',
      'left:' + from.left + 'px',
      'top:' + from.top + 'px',
      'width:' + from.width + 'px',
      'height:' + from.height + 'px',
      'object-fit:cover',
      'border-radius:8px',
      'z-index:2147483000',
      'pointer-events:none',
      'margin:0'
    ].join(';');
    document.body.appendChild(ghost);

    var dx = (to.left + to.width / 2) - (from.left + from.width / 2);
    var dy = (to.top + to.height / 2) - (from.top + from.height / 2);

    var flight = ghost.animate([
      { transform: 'translate(0,0) scale(1)', opacity: 1 },
      { transform: 'translate(' + (dx * 0.5) + 'px,' + (dy * 0.62) + 'px) scale(.6)', opacity: .95, offset: .6 },
      { transform: 'translate(' + dx + 'px,' + dy + 'px) scale(.15)', opacity: .1 }
    ], { duration: 620, easing: 'cubic-bezier(.4,.05,.6,1)' });

    var cleanup = function () {
      if (ghost.parentNode) ghost.remove();
    };

    flight.onfinish = function () {
      cleanup();
      if (target.animate) {
        target.animate([
          { transform: 'scale(1)' }, { transform: 'scale(1.25)' }, { transform: 'scale(1)' }
        ], { duration: 320, easing: 'ease-out' });
      }
    };

    // onfinish won't fire if the flight is cancelled — by the cart drawer
    // re-rendering, a navigation, or a background tab. Never strand the node.
    window.setTimeout(cleanup, 1200);
  }

  document.addEventListener('amb:cart-added', function (e) {
    var button = document.querySelector('.sticky-atc-button.amb-atc-loading');
    if (button) finishAdding(button, !!(e.detail && e.detail.ok));
  });

  /* ---- Wiring ---- */

  document.addEventListener('click', function (event) {
    var target = event.target;
    var hit = target && target.closest ? target.closest('.sticky-atc-button') : null;
    if (!hit) return;

    if (isBlocked()) {
      event.preventDefault();
      event.stopImmediatePropagation();
      promptForSize();
      return;
    }

    startAdding(hit);
  }, true);

  document.addEventListener('change', function () {
    window.setTimeout(sync, 0);
  }, true);

  function watch() {
    if (observer) observer.disconnect();
    observer = new MutationObserver(function () {
      if (!applying) sync();
    });

    var root = productRoot();
    if (root) observer.observe(root, { attributes: true, attributeFilter: ['class'] });

    // Dawn replaces the sticky bar wholesale on variant change, so watch its parent.
    var bar = stickyBar();
    var host = bar && bar.parentElement ? bar.parentElement : document.body;
    observer.observe(host, { childList: true, subtree: true });
  }

  function init() {
    watchCartAdds();
    sync();
    watch();

    /* The gate deselects the size by setting the `checked` property. That
       fires no change event and mutates no attribute, so neither our change
       listener nor the MutationObserver can see it — and if the gate happens
       to run a tick after us, we'd sample the DOM while "S" is still checked
       and never look again. Hence a bounded poll: dense while the page
       settles, then a slow heartbeat, then it stops. */
    var settleAt = [0, 60, 150, 300, 600, 1000, 1600, 2400, 3200];
    for (var i = 0; i < settleAt.length; i++) {
      window.setTimeout(function () { sync(); watch(); }, settleAt[i]);
    }

    var beats = 0;
    var heartbeat = window.setInterval(function () {
      sync();
      if (++beats >= 25) window.clearInterval(heartbeat); // stop after ~30s
    }, 1200);

    // Back from bfcache with stale state
    window.addEventListener('pageshow', function () { sync(); watch(); });
    window.addEventListener('load', function () { sync(); watch(); });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();